<template>
  <div class="group-management">
    <h2>Group Management</h2>
    
    <!-- Search Form -->
    <div class="search-section">
      <div class="search-form">
        <input 
          type="text" 
          v-model="searchName" 
          class="search-input" 
          placeholder="Search groups by name..."
          @keyup.enter="searchGroups"
        />
        <button @click="searchGroups" class="search-button">Search</button>
        <button @click="fetchGroups" class="reset-button">Reset</button>
      </div>
    </div>
    
    <!-- Add Group Form -->
    <button 
      @click="showAddGroupForm = !showAddGroupForm" 
      class="toggle-form-button"
    >
      {{ showAddGroupForm ? 'Cancel' : 'Add New Group' }}
    </button>
    
    <div v-if="showAddGroupForm" class="add-group-form">
      <h3>Add New Group</h3>
      <form @submit.prevent="addGroup">
        <div class="form-group">
          <label for="groupName">Group Name *</label>
          <input type="text" id="groupName" v-model="newGroup.groupName" required>
        </div>
        <div class="form-group">
          <label for="description">Description</label>
          <textarea id="description" v-model="newGroup.description" rows="3"></textarea>
        </div>
        <div class="form-actions">
          <button type="submit" class="submit-button">Add Group</button>
          <button type="button" @click="resetForm" class="reset-button">Reset</button>
        </div>
      </form>
    </div>
    
    <!-- Groups Table -->
    <div class="groups-table">
      <table v-if="groups.length > 0">
        <thead>
          <tr>
            <th>Group Name</th>
            <th>Description</th>
            <th>User Count</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="group in groups" :key="group.groupName">
            <td>{{ group.groupName }}</td>
            <td>{{ group.description || 'No description' }}</td>
            <td>{{ group.userCount || 0 }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else class="no-groups">
        <p v-if="isLoading">Loading groups...</p>
        <p v-else>No groups found</p>
      </div>
    </div>
  </div>
</template>

<script>
import { getGroups, searchGroups, createGroup } from '../../services/adminService'

export default {
  name: 'GroupManagement',
  data() {
    return {
      groups: [],
      isLoading: false,
      error: null,
      searchName: '',
      showAddGroupForm: false,
      newGroup: {
        groupName: '',
        description: ''
      }
    }
  },
  async created() {
    await this.fetchGroups()
  },
  methods: {
    async fetchGroups() {
      this.isLoading = true
      try {
        const response = await getGroups()
        this.groups = response.groups || []
        this.searchName = ''
      } catch (error) {
        console.error('Error fetching groups:', error)
        this.error = 'Failed to load groups'
      } finally {
        this.isLoading = false
      }
    },
    
    async searchGroups() {
      if (!this.searchName.trim()) {
        await this.fetchGroups()
        return
      }
      
      this.isLoading = true
      try {
        const response = await searchGroups(this.searchName)
        this.groups = response.groups || []
      } catch (error) {
        console.error('Error searching groups:', error)
        this.error = 'Failed to search groups'
      } finally {
        this.isLoading = false
      }
    },
    
    async addGroup() {
      try {
        await createGroup(this.newGroup)
        this.resetForm()
        this.showAddGroupForm = false
        await this.fetchGroups()
      } catch (error) {
        console.error('Error adding group:', error)
        this.error = 'Failed to add group'
      }
    },
    
    resetForm() {
      this.newGroup = {
        groupName: '',
        description: ''
      }
    }
  }
}
</script>

<style scoped>
.group-management {
  width: 100%;
}

h2 {
  margin-bottom: 1.5rem;
  color: var(--secondary);
}

h3 {
  margin-bottom: 1rem;
  color: var(--secondary);
}

/* Search section */
.search-section {
  margin-bottom: 1.5rem;
}

.search-form {
  display: flex;
  gap: 0.5rem;
}

.search-input {
  flex-grow: 1;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
}

/* Buttons */
button {
  cursor: pointer;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 4px;
  font-weight: 500;
}

.search-button {
  background-color: var(--primary);
  color: white;
}

.reset-button {
  background-color: #f0f0f0;
  color: #333;
}

.toggle-form-button {
  margin-bottom: 1rem;
  background-color: var(--secondary);
  color: white;
}

.submit-button {
  background-color: #2ecc71;
  color: white;
}

/* Add group form */
.add-group-form {
  background-color: #f9f9f9;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
}

.form-group input,
.form-group textarea {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.form-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

/* Groups table */
.groups-table {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
}

th, td {
  text-align: left;
  padding: 0.75rem;
  border-bottom: 1px solid #ddd;
}

th {
  background-color: #f5f5f5;
  font-weight: 600;
}

.no-groups {
  text-align: center;
  padding: 2rem;
  color: #666;
}
</style> 